import { useEffect, useState } from 'react';
import prodApi from '../../services/prodApi';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { 
  BookOpen, 
  Package,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  FileSpreadsheet,
  FileText,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


export const Kardex = () => {
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [kardexData, setKardexData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(true);

  const fetchItems = async () => {
    try {
      const response = await prodApi.get(`/inventario`);
      setItems(response.data);
    } catch (error) {
      toast.error('Error al cargar items');
    } finally {
      setLoadingItems(false);
    }
  };

  const fetchKardex = async (itemId) => {
    if (!itemId) {
      setKardexData(null);
      return;
    }
    
    setLoading(true);
    try {
      const response = await prodApi.get(`/inventario-kardex/${itemId}`);
      setKardexData(response.data);
    } catch (error) {
      toast.error('Error al cargar kardex');
      setKardexData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    if (selectedItemId) {
      fetchKardex(selectedItemId);
    }
  }, [selectedItemId]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
    }).format(value);
  };

  const getTipoIcon = (tipo) => {
    if (tipo === 'ingreso' || tipo === 'ajuste_entrada') {
      return <ArrowDownCircle className="h-4 w-4 text-green-600" />;
    } else if (tipo === 'salida' || tipo === 'ajuste_salida') {
      return <ArrowUpCircle className="h-4 w-4 text-red-500" />;
    }
    return <RefreshCw className="h-4 w-4 text-blue-500" />;
  };

  const getTipoBadge = (tipo) => {
    const labels = {
      'ingreso': 'Ingreso',
      'salida': 'Salida',
      'ajuste_entrada': 'Ajuste +',
      'ajuste_salida': 'Ajuste -'
    };
    const label = labels[tipo] || tipo;
    
    if (tipo === 'ingreso') {
      return <Badge className="bg-green-600">{label}</Badge>;
    } else if (tipo === 'salida') {
      return <Badge variant="destructive">{label}</Badge>;
    } else if (tipo === 'ajuste_entrada') {
      return <Badge className="bg-blue-500">{label}</Badge>;
    } else if (tipo === 'ajuste_salida') {
      return <Badge className="bg-orange-500">{label}</Badge>;
    }
    return <Badge>{label}</Badge>;
  };

  // Calcular totales - el backend devuelve cantidad (positiva/negativa) y tipo
  const totales = kardexData?.movimientos?.reduce((acc, m) => {
    if (m.tipo === 'ingreso') {
      acc.entradas += Math.abs(m.cantidad || 0);
      acc.costoEntradas += m.costo_total || 0;
    } else if (m.tipo === 'salida' || m.tipo === 'ajuste_salida') {
      acc.salidas += Math.abs(m.cantidad || 0);
      acc.costoSalidas += m.costo_total || 0;
    } else if (m.tipo === 'ajuste_entrada') {
      acc.entradas += Math.abs(m.cantidad || 0);
    }
    return acc;
  }, { entradas: 0, salidas: 0, costoEntradas: 0, costoSalidas: 0 }) || { entradas: 0, salidas: 0, costoEntradas: 0, costoSalidas: 0 };

  // Estado para loading de exportación
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Exportar a Excel (CSV)
  const handleExportExcel = async () => {
    if (!kardexData || !selectedItemId) return;
    setExportingExcel(true);
    try {
      const response = await prodApi.get(`/export/kardex/${selectedItemId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kardex_${kardexData.item.codigo}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Archivo Excel exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar a Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  // Exportar a PDF
  const handleExportPDF = () => {
    if (!kardexData) return;
    setExportingPDF(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Kardex de Inventario', pageWidth / 2, 20, { align: 'center' });
      
      // Info del item
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`${kardexData.item.codigo} - ${kardexData.item.nombre}`, pageWidth / 2, 28, { align: 'center' });
      
      // Fecha
      doc.setFontSize(10);
      doc.setTextColor(100);
      const fecha = new Date().toLocaleDateString('es-PE', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      doc.text(`Generado: ${fecha}`, pageWidth / 2, 35, { align: 'center' });
      
      // Línea separadora
      doc.setDrawColor(200);
      doc.line(14, 40, pageWidth - 14, 40);
      
      // Resumen
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text('Resumen', 14, 48);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Unidad de Medida: ${kardexData.item.unidad_medida}`, 14, 56);
      doc.text(`Stock Actual: ${kardexData.item.stock_actual}`, 14, 62);
      doc.text(`Total Entradas: +${totales.entradas}`, 14, 68);
      doc.text(`Total Salidas: -${totales.salidas}`, 100, 68);
      doc.text(`Costo Entradas: S/ ${totales.costoEntradas.toFixed(2)}`, 14, 74);
      doc.text(`Costo Salidas: S/ ${totales.costoSalidas.toFixed(2)}`, 100, 74);
      
      // Tabla de movimientos
      const getTipoLabel = (tipo) => {
        const labels = {
          'ingreso': 'Ingreso',
          'salida': 'Salida',
          'ajuste_entrada': 'Ajuste (+)',
          'ajuste_salida': 'Ajuste (-)'
        };
        return labels[tipo] || tipo;
      };

      const tableData = kardexData.movimientos.map(mov => [
        formatDate(mov.fecha),
        getTipoLabel(mov.tipo),
        mov.documento || mov.observaciones || '-',
        (mov.tipo === 'ingreso' || mov.tipo === 'ajuste_entrada') ? `+${Math.abs(mov.cantidad)}` : '',
        (mov.tipo === 'salida' || mov.tipo === 'ajuste_salida') ? `-${Math.abs(mov.cantidad)}` : '',
        mov.saldo.toString(),
        mov.costo_unitario > 0 ? `S/ ${mov.costo_unitario.toFixed(2)}` : '-',
        mov.costo_total > 0 ? `S/ ${mov.costo_total.toFixed(2)}` : '-'
      ]);
      
      autoTable(doc, {
        startY: 82,
        head: [['Fecha', 'Tipo', 'Documento', 'Entrada', 'Salida', 'Saldo', 'Costo Unit.', 'Costo Total']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8,
        },
        bodyStyles: {
          fontSize: 7,
        },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 20 },
          2: { cellWidth: 35 },
          3: { cellWidth: 18, halign: 'right' },
          4: { cellWidth: 18, halign: 'right' },
          5: { cellWidth: 18, halign: 'right', fontStyle: 'bold' },
          6: { cellWidth: 22, halign: 'right' },
          7: { cellWidth: 22, halign: 'right' },
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        margin: { left: 14, right: 14 },
      });
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Página ${i} de ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
        doc.text(
          'Sistema de Producción Textil',
          14,
          doc.internal.pageSize.getHeight() - 10
        );
      }
      
      doc.save(`kardex_${kardexData.item.codigo}_${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success('PDF exportado correctamente');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al exportar PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="kardex-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Kardex de Inventario
          </h2>
          <p className="text-muted-foreground">Historial detallado de movimientos por item</p>
        </div>
        {kardexData && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              disabled={exportingExcel}
              data-testid="export-kardex-excel"
            >
              {exportingExcel ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              )}
              Exportar Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={exportingPDF}
              data-testid="export-kardex-pdf"
            >
              {exportingPDF ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              Exportar PDF
            </Button>
          </div>
        )}
      </div>

      {/* Selector de Item */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Seleccionar Item</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Label className="mb-2 block">Item de Inventario</Label>
            <Select 
              value={selectedItemId} 
              onValueChange={setSelectedItemId}
              disabled={loadingItems}
            >
              <SelectTrigger data-testid="select-item-kardex">
                <SelectValue placeholder={loadingItems ? "Cargando..." : "Seleccionar item..."} />
              </SelectTrigger>
              <SelectContent>
                {items.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    <span className="font-mono mr-2">{item.codigo}</span>
                    {item.nombre}
                    <span className="ml-2 text-muted-foreground">(Stock: {item.stock_actual})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contenido del Kardex */}
      {loading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Cargando kardex...
          </CardContent>
        </Card>
      ) : kardexData ? (
        <>
          {/* Info del Item */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Package className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm text-muted-foreground">{kardexData.item.codigo}</span>
                    <Badge variant="outline">{kardexData.item.unidad_medida}</Badge>
                  </div>
                  <h3 className="text-xl font-semibold">{kardexData.item.nombre}</h3>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Stock Actual</p>
                  <p className="text-3xl font-bold text-primary">{kardexData.item.stock_actual}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Entradas</p>
                    <p className="text-xl font-bold text-green-600">+{totales.entradas}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <TrendingDown className="h-6 w-6 text-red-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Salidas</p>
                    <p className="text-xl font-bold text-red-500">-{totales.salidas}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <ArrowDownCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Costo Entradas</p>
                    <p className="text-lg font-bold">{formatCurrency(totales.costoEntradas)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <ArrowUpCircle className="h-6 w-6 text-red-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Costo Salidas</p>
                    <p className="text-lg font-bold">{formatCurrency(totales.costoSalidas)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabla Kardex */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Movimientos</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="data-table-header">
                      <TableHead>Fecha</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead className="text-right">Entrada</TableHead>
                      <TableHead className="text-right">Salida</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                      <TableHead className="text-right">Costo Unit.</TableHead>
                      <TableHead className="text-right">Costo Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kardexData.movimientos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No hay movimientos registrados
                        </TableCell>
                      </TableRow>
                    ) : (
                      kardexData.movimientos.map((mov, index) => (
                        <TableRow key={mov.id || index} className="data-table-row" data-testid={`kardex-row-${index}`}>
                          <TableCell className="font-mono text-sm">
                            {formatDate(mov.fecha)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getTipoIcon(mov.tipo)}
                              {getTipoBadge(mov.tipo)}
                            </div>
                          </TableCell>
                          <TableCell>
                            {mov.documento || (
                              <span className="text-muted-foreground text-sm truncate max-w-[150px] block">
                                {mov.observaciones || '-'}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {(mov.tipo === 'ingreso' || mov.tipo === 'ajuste_entrada') ? (
                              <span className="text-green-600 font-semibold">+{Math.abs(mov.cantidad)}</span>
                            ) : (
                              <Minus className="h-4 w-4 text-muted-foreground mx-auto" />
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {(mov.tipo === 'salida' || mov.tipo === 'ajuste_salida') ? (
                              <span className="text-red-500 font-semibold">-{Math.abs(mov.cantidad)}</span>
                            ) : (
                              <Minus className="h-4 w-4 text-muted-foreground mx-auto" />
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold bg-muted/30">
                            {mov.saldo}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {mov.costo_unitario > 0 ? formatCurrency(mov.costo_unitario) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {mov.costo_total > 0 ? formatCurrency(mov.costo_total) : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : selectedItemId ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Error al cargar el kardex
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Selecciona un item para ver su kardex</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
export default Kardex;
